import { map } from 'lodash'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useParams } from 'react-router-dom'

import { Header, PageTitle } from '@/components'
import { Button, Card, Checkbox } from '@/components/ui'
import { Plus, Trash } from 'lucide-react'

import { AssetsModal } from './AssetsModal'
import { Icon } from '../../../common/components/Icon'
import Loader from '../../../common/components/Loader'
import { Assets } from '../../../common/models'
import { IColumn, Table } from '../../../core/components/Table'
import { sanitizeDate } from '../../../core/constants/times'
import { useTableParams } from '../../../core/hooks/table-params'
import { useCheckPermission } from '../../../core/hooks/use-check-permission'
import { deleteAssets, getAssets } from '../../../core/services/assets'
import { useAppSelector } from '../../../store'
import { DeleteConfirmationModal } from '../DeleteConfirmationModal'
import { MessageBox } from '../messages/MessageBox'
import { Pagination } from '../Pagination'

export const AssetsPage: React.FC = (): JSX.Element => {
  const {
    page,
    setPage,
    size,
    setSize,
    totalElements,
    setTotalElemenets,
    sortBy,
    setSortBy,
  } = useTableParams()
  const [assets, setAssets] = useState<Assets[]>([])
  const [showAssetModal, setAssetModal] = useState<boolean>(false)
  const [currentAsset, setCurrentAsset] = useState<Assets>()
  const [isCheckAll, setIsCheckAll] = useState(false)
  const [isCheck, setIsCheck] = useState<string[]>([])
  const [showDeleteConfirmationModal, setDeleteConfirmationModal] =
    useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const [sortDirection, setSortDirection] = useState<
    'ASC' | 'DESC' | undefined
  >(undefined)

  const { hasEditPermission } = useCheckPermission({ page: 'UPLOAD_IMAGES' })

  const [isError, setIsError] = useState<boolean>(false)
  const [showMessageBox, setShowMessageBox] = useState<boolean>(false)
  const [message, setMessage] = useState<string>('')

  const { festivalName } = useParams()
  const { festivals: storeFestivals } = useAppSelector((s) => s.app)
  const festivalFullName = storeFestivals.find(
    (f) => f.platformName === festivalName,
  )?.name

  const getData = useCallback(async () => {
    setLoading(true)
    try {
      const response = await getAssets({
        page,
        size,
        sortBy,
        sortDirection,
      })
      setTotalElemenets(response.data.totalElements)
      setAssets(response.data.content)
    } catch (e) {
      if (e instanceof Error) {
        setIsError(true)
        setShowMessageBox(true)
        setMessage(e.message)
      } else {
        console.error(e)
      }
    } finally {
      setLoading(false)
    }
  }, [page, size, sortBy, sortDirection, setTotalElemenets])

  const handleSortBy = (sort: string) => {
    if (!hasEditPermission) {
      return
    }
    if (sort === sortBy) {
      if (sortDirection === 'DESC') {
        setSortDirection('ASC')
      } else {
        setSortDirection(undefined)
        setSortBy(undefined)
      }
    } else {
      setSortDirection('DESC')
      setSortBy(sort)
    }
  }

  useEffect(() => {
    getData()
  }, [getData])

  const handleDelete = () => {
    if (!hasEditPermission) {
      return
    }
    if (isCheck.length > 0) {
      setDeleteConfirmationModal(true)
    }
  }

  const handleSelectAll = useCallback(() => {
    if (!hasEditPermission) {
      return
    }
    const nextChecked = !isCheckAll
    setIsCheckAll(nextChecked)
    setIsCheck(nextChecked ? map(assets, (photo) => photo.id) : [])
  }, [assets, hasEditPermission, isCheckAll])

  const handleClick = useCallback(
    (id: string, checked: boolean) => {
      if (!hasEditPermission) {
        return
      }
      setIsCheck((prev) =>
        checked ? [...prev, id] : prev.filter((item) => item !== id),
      )
    },
    [hasEditPermission],
  )

  const deleteFunc = () => {
    if (!hasEditPermission) {
      return
    }
    // prettier-ignore
    (async () => {
      setLoading(true)
      try {
        const { status } = await deleteAssets(isCheck)
        getData()
        setIsCheck([])
        setIsCheckAll(false)
        if (status === 204) {
          setIsError(false)
          setShowMessageBox(true)
          setMessage('Delete Successful')
        }
      } catch (e) {
        if (e instanceof Error) {
          setIsError(true)
          setShowMessageBox(true)
          setMessage(e.message)
        } else {
          console.error(e)
        }
      } finally {
        setLoading(false)
      }
    })()
  }

  const changePage = async (newPage: number) => {
    setPage(newPage)
  }

  const renderFormatPreview = useCallback((format: string, link: string) => {
    const iconName = format === 'PDF' ? 'pdf' : format === 'ZIP' ? 'zip' : null
    if (!iconName) {
      return (
        <img
          src={link}
          className="h-[70px] w-[108px] rounded-[10px] object-contain"
        />
      )
    }

    return (
      <div className="flex w-full items-center justify-center">
        <div className="flex items-center justify-center w-[70px] h-[70px] rounded-[10px] border border-monochrome-120">
          <Icon
            icon={iconName}
            width="27px"
            height="36px"
            viewBox="0 0 27 36"
          />
        </div>
      </div>
    )
  }, [])

  const columns: IColumn[] = useMemo(
    () => [
      {
        title: (
          <Checkbox
            onCheckedChange={handleSelectAll}
            checked={isCheckAll}
            disabled={!hasEditPermission}
          />
        ),
        accessor: '',
        render: (row) => (
          <Checkbox
            onCheckedChange={(checked: boolean) => handleClick(row.id, checked)}
            checked={isCheck.includes(row.id)}
            disabled={!hasEditPermission}
          />
        ),
        width: '16px',
      },
      {
        title: 'Image',
        accessor: '',
        width: '125px',
        render: (row) => renderFormatPreview(row.format, row.link),
      },
      {
        title: 'Name',
        accessor: 'name',
        render: (row) => {
          return row.link.substring(row.link.lastIndexOf('/') + 1)
        },
      },
      {
        title: 'Uploaded Date',
        accessor: 'uploadedAt',
        sortable: true,
        render: (row) =>
          row.publishedAt && sanitizeDate(new Date(row.publishedAt)),
      },
      {
        title: 'Size',
        accessor: 'width',
        sortable: true,
        render: (row) =>
          row.width && row.height ? `${row.width}x${row.height}` : '-',
      },
      {
        title: 'Format',
        accessor: 'format',
        sortable: true,
      },
      {
        title: 'Weight',
        accessor: 'size',
        sortable: true,
        render: (row) => `${Math.ceil(row.size / 1024)}kb`,
      },
      {
        title: 'Link',
        accessor: 'link',
        render: (row) => (
          <Button
            variant="secondary"
            size="sm"
            disabled={!hasEditPermission}
            onClick={() => {
              navigator.clipboard.writeText(row.link)
            }}
          >
            Copy Link
          </Button>
        ),
      },
    ],
    [
      handleClick,
      handleSelectAll,
      hasEditPermission,
      isCheck,
      renderFormatPreview,
    ],
  )

  return (
    <>
      <Header
        pageTitle="Assets"
        actions={
          <>
            {hasEditPermission && isCheck.length >= 1 && (
              <Button variant="destructive" onClick={handleDelete}>
                Delete
                <Icon
                  icon="close-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
            {hasEditPermission && (
              <Button
                variant="primary-bordered"
                onClick={() => {
                  setAssetModal(true)
                  setCurrentAsset(undefined)
                }}
              >
                Add New
                <Icon
                  icon="plus-circle"
                  width="18"
                  height="18"
                  viewBox="0 0 18 18"
                  fill="none"
                />
              </Button>
            )}
          </>
        }
      />
      {loading && <Loader />}
      <Card className="p-5 !bg-monochrome-4-60">
        <PageTitle title="Assets" className="mb-5" />
        <Table
          columnWidth="15%"
          handleSortBy={handleSortBy}
          isCheck={isCheck}
          columns={columns}
          data={assets}
        />
      </Card>
      <Pagination
        size={size}
        totalElements={totalElements}
        setSize={setSize}
        page={page + 1}
        changePage={changePage}
      />
      <AssetsModal
        showCreateModal={showAssetModal}
        setShowCreateModal={setAssetModal}
        page={page + 1}
        size={size}
        setTotalElemenets={setTotalElemenets}
        setAssets={setAssets}
        asset={currentAsset}
        setLoading={setLoading}
      />
      <DeleteConfirmationModal
        showDeleteConfirmationModal={showDeleteConfirmationModal}
        setDeleteConfirmationModal={setDeleteConfirmationModal}
        deleteTitle={'Asset'}
        deleteFunc={deleteFunc}
      />
      <MessageBox
        message={message}
        setShowMessageBox={setShowMessageBox}
        showMessageBox={showMessageBox}
        isError={isError}
      />
    </>
  )
}
